import React, { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Turnstile from '../Turnstile/Turnstile';
import './Footer.css';

const initialFormData = {
  name: '',
  email: '',
  type: '',
  message: '',
  website: '',
};

const Footer = () => {
  const location = useLocation();
  const isGamesPage = location.pathname === '/games';
  const isHomePage = location.pathname === '/';
  const isAboutPage = location.pathname === '/about';
  const isContactPage = location.pathname === '/contact';

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const formStartedAtRef = useRef(Date.now());

  const validateForm = useCallback((data) => {
    const nextErrors = {};

    if (!data.name.trim()) {
      nextErrors.name = 'Name is required';
    } else if (data.name.trim().length < 2) {
      nextErrors.name = 'Name must be at least 2 characters';
    }

    if (!data.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (!data.type) {
      nextErrors.type = 'Please choose a request type';
    }

    if (!data.message.trim()) {
      nextErrors.message = 'Message is required';
    } else if (data.message.trim().length < 10) {
      nextErrors.message = 'Message must be at least 10 characters';
    }

    return nextErrors;
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  }, []);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    if (token) {
      setTurnstileError('');
    }
  }, []);

  const handleTurnstileUnavailable = useCallback((message) => {
    setTurnstileToken('');
    setTurnstileError(message);
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const validationErrors = validateForm(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      if (!turnstileToken) {
        setTurnstileError('Please complete the verification.');
        return;
      }

      setIsSubmitting(true);
      setFormMessage(null);
      setErrors({});

      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            email: formData.email.trim(),
            type: formData.type,
            message: formData.message.trim(),
            website: formData.website,
            startedAt: formStartedAtRef.current,
            turnstileToken,
          }),
        });

        const result = await response.json().catch(() => null);

        if (response.ok) {
          setFormMessage({
            type: 'success',
            text: result?.message || 'Message sent successfully.',
          });
          setFormData(initialFormData);
          formStartedAtRef.current = Date.now();
        } else {
          setFormMessage({
            type: 'error',
            text: result?.message || 'Failed to send message. Please try again.',
          });
        }
      } catch {
        setFormMessage({
          type: 'error',
          text: 'Network error. Please check your connection and try again.',
        });
      } finally {
        setIsSubmitting(false);
        setTurnstileToken('');
        setTurnstileResetKey((current) => current + 1);
      }
    },
    [formData, turnstileToken, validateForm]
  );

  // Use different images for different pages
  let backgroundImage;
  if (isGamesPage) {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/contact-bg_2.png)`;
  } else if (isHomePage || isAboutPage || isContactPage) {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/contact-bg.png)`;
  } else {
    backgroundImage = `url(${process.env.PUBLIC_URL}/images/contact-bg_3.png)`;
  }
  const backgroundPosition = isContactPage ? 'center 20%' : 'center';
  const backgroundTop = isContactPage ? '-35%' : '-25%';
  const backgroundBottom = isContactPage ? '-15%' : '-25%';

  return (
    <footer className="container-fluid px-5" style={{position: 'relative'}}>
      <div 
        style={{
          position: 'absolute',
          top: backgroundTop,
          left: 0,
          right: 0,
          bottom: backgroundBottom,
          backgroundImage: backgroundImage,
          backgroundSize: 'cover',
          backgroundPosition: backgroundPosition,
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#111111',
          opacity: 0.9,
          zIndex: 0
        }}
      ></div>
      <div className="container" style={{position: 'relative', zIndex: 1}}>
        <div className="row align-items-start">
          <div className="col-md-3 footer1 d-flex wow bounceInLeft" data-wow-delay=".25s">
            <div className="d-flex flex-wrap align-content-center">
              <Link to="/">
                <img src="/images/Pixward_Logo.png" alt="Pixward Studios" style={{maxHeight: '75px', width: 'auto', marginBottom: 'calc(1rem - 25px)'}} />
              </Link>
              <p>Pixward Studios - Creating immersive VR experiences for training, games, and virtual production.</p>
              <p>&copy; {new Date().getFullYear()} Pixward Studios. All rights reserved.</p>
            </div>
          </div>
          <div className="col-md-6 footer2 wow bounceInUp" data-wow-delay=".25s" id="contact" style={{position: 'relative', zIndex: 2}}>
            <div className="form-box">
              <h4>LET'S TALK ABOUT YOUR PROJECT</h4>
              <form onSubmit={handleSubmit} noValidate>
                <div className="contact-honeypot" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <table className="table table-responsive d-table">
                  <tbody>
                    <tr>
                      <td style={{ width: '50%', paddingRight: '10px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            name="name"
                            className="form-control"
                            placeholder="NAME"
                            value={formData.name}
                            onChange={handleInputChange}
                            maxLength={100}
                            autoComplete="name"
                            aria-invalid={Boolean(errors.name)}
                          />
                          {errors.name && <small className="form-error" style={{ position: 'absolute', top: '100%', left: '0', marginTop: '4px', zIndex: 10, pointerEvents: 'none' }}>{errors.name}</small>}
                        </div>
                      </td>
                      <td style={{ width: '50%', paddingLeft: '10px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="email"
                            name="email"
                            className="form-control"
                            placeholder="EMAIL"
                            value={formData.email}
                            onChange={handleInputChange}
                            maxLength={254}
                            autoComplete="email"
                            aria-invalid={Boolean(errors.email)}
                          />
                          {errors.email && <small className="form-error" style={{ position: 'absolute', top: '100%', left: '0', marginTop: '4px', zIndex: 10, pointerEvents: 'none' }}>{errors.email}</small>}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ height: '24px', padding: 0 }}></td>
                    </tr>
                    <tr>
                      <td colSpan="2">
                        <div style={{ position: 'relative' }}>
                          <select
                            name="type"
                            className="form-control"
                            value={formData.type}
                            onChange={handleInputChange}
                            aria-invalid={Boolean(errors.type)}
                          >
                            <option value="" disabled>
                              SELECT REQUEST TYPE
                            </option>
                            <option value="VR Training & Simulations">VR Training & Simulations</option>
                            <option value="Game Development">Game Development</option>
                            <option value="Virtual Production">Virtual Production</option>
                          </select>
                          {errors.type && <small className="form-error" style={{ position: 'absolute', top: '100%', left: '0', marginTop: '4px', zIndex: 10, pointerEvents: 'none' }}>{errors.type}</small>}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ height: '24px', padding: 0 }}></td>
                    </tr>
                    <tr>
                      <td colSpan="2">
                        <div style={{ position: 'relative' }}>
                          <textarea
                            name="message"
                            className="form-control"
                            placeholder="MESSAGE"
                            rows="4"
                            value={formData.message}
                            onChange={handleInputChange}
                            maxLength={2000}
                            aria-invalid={Boolean(errors.message)}
                          ></textarea>
                          {errors.message && <small className="form-error" style={{ position: 'absolute', top: '100%', left: '0', marginTop: '4px', zIndex: 10, pointerEvents: 'none' }}>{errors.message}</small>}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ height: '24px', padding: 0 }}></td>
                    </tr>
                    <tr>
                      <td colSpan="2" className="text-center">
                        <div className="turnstile-wrapper">
                          <Turnstile
                            key={turnstileResetKey}
                            onVerify={handleTurnstileVerify}
                            onUnavailable={handleTurnstileUnavailable}
                          />
                          {turnstileError && (
                            <small className="form-error turnstile-error">
                              {turnstileError}
                            </small>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ height: '18px', padding: 0 }}></td>
                    </tr>
                    <tr>
                      <td colSpan="2" className="text-center">
                        <button
                          type="submit"
                          disabled={isSubmitting || !turnstileToken}
                          className={`btn text-uppercase btn-demo ${isGamesPage ? 'games-gradient-button' : ''}`}
                          style={isGamesPage ? {
                            backgroundColor: '#f85aff',
                            borderColor: '#f85aff',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            color: '#fff',
                            borderRadius: 0,
                            textAlign: 'center',
                            opacity: isSubmitting || !turnstileToken ? 0.75 : 1,
                            minWidth: '200px'
                          } : {
                            backgroundColor: '#ff6600',
                            borderColor: '#ff6600',
                            color: '#fff',
                            borderRadius: 0,
                            textAlign: 'center',
                            opacity: isSubmitting || !turnstileToken ? 0.75 : 1,
                            minWidth: '200px'
                          }}
                        >
                          {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </form>
              <div style={{ minHeight: '30px', marginTop: '10px' }}>
                {formMessage && (
                  <p className={`form-feedback m-0 ${formMessage.type === 'success' ? 'form-feedback-success' : 'form-feedback-error'}`}>
                    {formMessage.text}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-3 footer3 wow bounceInRight" data-wow-delay=".25s">
            <h5>ADDRESS</h5>
            <p>Harju maakond, Tallinn, Kesklinna linnaosa, Vesivärava tn 50-201, 10152</p>
            <h5>EMAIL</h5>
            <p>contact@pixward.com</p>
            <div className="social-links">
              <ul className="nav nav-item">
                <li>
                  <a href="https://www.artstation.com/pixwardstudio" target="_blank" rel="noopener noreferrer" className="btn btn-secondary mr-1 mb-2">
                    <img src="/images/artstation.svg" alt="Artstation" className="social-logo social-logo-artstation" />
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/company/pixward-studios" target="_blank" rel="noopener noreferrer" className="btn btn-secondary mr-1 ml-1 mb-2">
                    <img src="/images/linkedin-big-logo.svg" alt="LinkedIn" className="social-logo" />
                  </a>
                </li>
                <li>
                  <a href="https://www.youtube.com/@pixwardstudios" target="_blank" rel="noopener noreferrer" className="btn btn-secondary mr-1 ml-1 mb-2 social-link-youtube">
                    <img src="/images/youtube-123.svg" alt="YouTube" className="social-logo social-logo-youtube" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

